import { Injectable, inject } from '@angular/core';
import { map, Observable, of } from 'rxjs';
import { CaseAuthorizationState } from '../../states/case-authorization.state';
import { CasePermission } from '../../../domain/enums/case-permission.enum';
import { AuthorizationRepository } from '../../../infrastructure/repositories/authorization.repository';
import { CaseRole } from '../../../domain/enums/case-role.enum';

/**
 * Use case for getting case permissions.
 * Fetches user's case role and permissions from the API, caches them in state,
 * and returns the permissions array.
 * Implements deduplication to prevent multiple simultaneous API calls.
 */
@Injectable({ providedIn: 'root' })
export class GetCasePermissionsUseCase {
  private readonly repository = inject(AuthorizationRepository);
  private readonly state = inject(CaseAuthorizationState);
  private readonly context = 'cases';

  /**
   * Gets case permissions for the current user.
   * Saves role and permissions to state, but returns only permissions.
   * Uses cached state unless explicitly forced to refresh.
   *
   * @param forceRefresh - If true, forces a fresh API call even if data is already loaded
   * @returns Observable that emits the user's case permissions
   */
  public execute(forceRefresh = false): Observable<CasePermission[]> {
    // Use cache unless explicitly forced to refresh
    if (!this.state.isLoaded() || forceRefresh) {
      return this.repository.getCurrentUserAuthorization(this.context).pipe(
        map(authData => {
          const role = authData.role as CaseRole;
          const permissions = authData.permissions as CasePermission[];
          this.state.setAuthorization(role, permissions, authData.roleName, authData.dashboardViewTypeName);
          return permissions;
        })
      );
    }
    return of(this.state.permissions());
  }
}
